import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { GetFeatureComparisonPayload, NoveltySimilarRepository, UpdateNoveltySimilarListPayload } from '../novelty-similar-repository';
import { UpdateNoveltySimilarListApi, type UpdateNoveltySimilarFinalResultDto } from '@/infrastructure/api-functions/core-drafting-api/update-novelty-similar-list-api';
import type { MatchedTechFeature, PatentProfileWithMatchedTechFeature } from '../../types';
import type { ComparisonFeature, FinalResult } from '@/infrastructure/api-functions/core-drafting-api/search-novelty-result-api';
import { NoveltyFeatureComparisonApi } from '@/infrastructure/api-functions/core-drafting-api/novelty-feature-comparison-api.ts';

@injectable()
export class NoveltySimilarRepositoryImpl implements NoveltySimilarRepository {
    constructor(
        @inject(UpdateNoveltySimilarListApi)
        private readonly _updateNoveltySimilarListApi: UpdateNoveltySimilarListApi,
        @inject(NoveltyFeatureComparisonApi)
        private readonly _noveltyFeatureComparisonApi: NoveltyFeatureComparisonApi,
    ) { }

    async updateNoveltySimilarList(payload: UpdateNoveltySimilarListPayload): Promise<PatentProfileWithMatchedTechFeature[]> {
        return this._updateNoveltySimilarListApi.updateNoveltySimilarList({
            task_id: payload.taskId,
            final_result: this._convertTechFeature2FinalResult(payload.techFeatures),
            is_patent_confirm: payload.isPatentConfirm,
        }).then((response) => {
            return this._convertFinalResult2TechFeature(response);
        });
    }

    private _convertTechFeature(feature: ComparisonFeature) {
        return {
            techFeature: feature.tech_feature,
            comparisonFeature: feature.comparison_feature,
            score: feature.score,
            publicScore: feature.public_score,
            similar: feature.similar,
            type: feature.type,
            originalType: feature.original_type,
            reason: feature.reason || '',
            originalReason: feature.original_reason || '',
        } as MatchedTechFeature;
    }

    private _convertTechFeature2FinalResult(techFeatures: PatentProfileWithMatchedTechFeature[]): UpdateNoveltySimilarFinalResultDto[] {
        return techFeatures.map((techFeature) => ({
            patent_id: techFeature.patentId,
            features: techFeature.matchedTechFeatures.map((feature) => ({
                comparison_feature: feature.comparisonFeature,
                tech_feature: feature.techFeature,
                similar: feature.similar,
                type: feature.type,
                original_type: feature.originalType,
                reason: feature.reason || '',
                original_reason: feature.originalReason || '',
            })),
            most_similar: techFeature.mostSimilar ?? false,
            selected: techFeature.selected,
            user_added: techFeature.userAdded,
        }));
    }

    private _convertFinalResult2TechFeature(data: FinalResult[]): PatentProfileWithMatchedTechFeature[] {
        const result: PatentProfileWithMatchedTechFeature[] = [];
        data.forEach((item) => {
            result.push({
                patentId: item.patent_id,
                matchedTechFeatures: item.features
                    .filter((feature) => feature.score > 0)
                    .map((feature) => this._convertTechFeature(feature)),
                selected: item.selected,
                mostSimilar: item.most_similar,
                comparativeLiteratureType: item.comparative_literature_type,
                lang: item.lang,
                publicScore: item.public_score,
                extracted: item.features.length > 0,
                userAdded: item.user_added,
            });
        });
        return result;
    }

    async getFeatureComparison(payload:GetFeatureComparisonPayload): Promise<PatentProfileWithMatchedTechFeature[]> {
        const result = await this._noveltyFeatureComparisonApi.getFeatureComparison({
            task_id: payload.task_id,
            patent_pns: payload.patent_pns,
            is_add_patent: payload.is_add_patent ?? false,
            add_patent_selected: payload.add_patent_selected ?? false,
        });
        return (result?.final_res || []).map(p => {
            const features = p.features.map(feat => Object.assign(this._convertTechFeature(feat), { similar: true }));
            return {
                patentId: p.patent_id,
                matchedTechFeatures: features.filter((feature) => feature.score > 0),
                selected: false,
                mostSimilar: false,
                comparativeLiteratureType: p.comparativeLiteratureType!,
                lang: p.lang,
                extracted: features.length > 0,
                publicScore: p.public_score,
                userAdded: false,
            };
        });
    }
}
